import React, { useState, useCallback, useEffect } from 'react';
import { Tab } from 'rbx';
import { useStoreState } from 'easy-peasy';
import styled from 'styled-components';
import { forEach } from 'lodash';
import tzLookup from 'tz-lookup';
import moment from 'moment-timezone';

import { Box, Tabs } from '../../../components/elements';
import Active from './Active';
import Ready from './Ready';
import Scheduled from './Scheduled';
import Cancelled from './Cancelled';
import Header from './Header';

import StartStopModal from './StartStopModal';

import { getMenus, fetchOrderCounts } from './helpers';

const Container = styled.div``;

const calculateIsOpen = ({ place }) => {
  let isPlaceOpen = false;
  const uniqHours = [
    place.hours_pickup,
    place.hours_dinein,
    place.hours_delivery,
  ];
  forEach(uniqHours, item => {
    if (item === 'OPENED') {
      isPlaceOpen = true;
    }
  });
  return isPlaceOpen;
};

const MainForm = props => {
  const { userId } = useStoreState(state => state.auth);
  const [isActiveTab, setIsActiveTab] = useState('new');

  const [isActiveStartStopModal, setIsActiveStartStopModal] = useState(false);
  const [newOrderNotificationCount, setNewOrderNotificationCount] = useState(0);

  const [orderCount, setOrderCount] = useState({
    receivedOrder: 0,
    readyOrder: 0,
    cancelOrder: 0,
    confirmedOrder: 0,
    completedOrder: 0,
    scheduledOrder: 0,
  });

  const [loadingOrderCount, setLoadingOrderCount] = React.useState(true);

  const [menus, setMenus] = useState([]);
  const [takeOrder, setTakeOrder] = useState();
  const {
    place,
    currentSubscriptionData,
    setCurrentSubscriptionData,
    setOrders,
    refetchPlace,
    placeId,
    currentSubscribeToMenuOrderMoveToActive,
    setCurrentSubscribeToMenuOrderMoveToActive,
  } = props;

  const fetchCount = useCallback(async () => {
    setLoadingOrderCount(true);
    const setDateHours = dateObject => {
      dateObject.setHours(0, 0, 0, 0);
      return dateObject;
    };

    const startDateISOString = dateObject =>
      setDateHours(dateObject).toISOString();

    try {
      const countObj = await fetchOrderCounts({
        place,
        placeId,
        userId,
        dateRange: {
          start_date: startDateISOString(new Date()),
          end_date: moment()
            .add(1, 'day')
            .toISOString(),
        },
      });
      if (countObj) {
        setOrderCount(countObj);
        setNewOrderNotificationCount(countObj.receivedOrder);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingOrderCount(false);
    }
  }, []);

  const fetchMenus = useCallback(async () => {
    try {
      const menuList = await getMenus({
        placeId: place.place_id,
        userId,
      });

      const IsOpen = calculateIsOpen({ place });
      setTakeOrder(IsOpen);
      setMenus(
        menuList.map(item => ({
          ...item,
          label: `${item.service_type} - ${item.name}`,
          value: item.menu_id,
        })),
      );
    } catch (err) {
      console.log('err', err);
    }
  }, []);

  useEffect(() => {
    const lat = parseFloat(place.latitude).toFixed(5);
    const long = parseFloat(place.longitude).toFixed(5);
    const timeZone = tzLookup(lat, long);
    moment.tz.setDefault(timeZone);
    fetchMenus();
  }, []);

  useEffect(() => {
    fetchCount();
  }, [isActiveTab, currentSubscriptionData]);

  return (
    <>
      {isActiveStartStopModal && (
        <StartStopModal
          isActive={isActiveStartStopModal}
          onClose={() => setIsActiveStartStopModal(false)}
          placeId={place.place_id}
          place={place}
          menus={menus}
          fetchMenus={fetchMenus}
          refetchPlace={refetchPlace}
        />
      )}
      <Header
        takeOrder={takeOrder}
        receivedOrdersLength={newOrderNotificationCount}
        placeName={place.name}
        placeLocation={`${place.city}, ${place.post_code} | ${place.country} `}
        setIsActiveStartStopModal={setIsActiveStartStopModal}
      />

      <Box>
        <Tabs className="tabs is-large is-fullwidth">
          <Tab
            active={isActiveTab === 'new'}
            onClick={() => setIsActiveTab('new')}
          >
            ACTIVE ( {orderCount.receivedOrder + orderCount.confirmedOrder} )
          </Tab>

          <Tab
            style={{ paddingLeft: '4rem' }}
            active={isActiveTab === 'ready'}
            onClick={() => setIsActiveTab('ready')}
          >
            READY ( {orderCount.readyOrder} )
          </Tab>

          <Tab
            style={{ paddingLeft: '4rem' }}
            active={isActiveTab === 'scheduled'}
            onClick={() => setIsActiveTab('scheduled')}
          >
            SCHEDULED ( {orderCount.scheduledOrder} )
          </Tab>

          <Tab
            style={{ paddingLeft: '4rem' }}
            active={isActiveTab === 'cancelled'}
            onClick={() => setIsActiveTab('cancelled')}
          >
            CANCELLED ( {orderCount.cancelOrder} )
          </Tab>
        </Tabs>
        <Container>
          <>
            {isActiveTab === 'new' && !loadingOrderCount && (
              <Active
                isActiveTab={isActiveTab}
                {...props}
                currentSubscriptionData={currentSubscriptionData}
                setOrders={setOrders}
                orderCount={orderCount}
                handleDecrementCount={() =>
                  setNewOrderNotificationCount(newOrderNotificationCount - 1)
                }
                setCurrentSubscriptionData={setCurrentSubscriptionData}
                setOrderCount={setOrderCount}
                currentSubscribeToMenuOrderMoveToActive={
                  currentSubscribeToMenuOrderMoveToActive
                }
                setCurrentSubscribeToMenuOrderMoveToActive={
                  setCurrentSubscribeToMenuOrderMoveToActive
                }
              />
            )}
            {isActiveTab === 'ready' && (
              <Ready
                isActiveTab={isActiveTab}
                {...props}
                orderCount={orderCount}
                currentSubscribeToMenuOrderMoveToActive={
                  currentSubscribeToMenuOrderMoveToActive
                }
                setCurrentSubscribeToMenuOrderMoveToActive={
                  setCurrentSubscribeToMenuOrderMoveToActive
                }
              />
            )}
            {isActiveTab === 'scheduled' && !loadingOrderCount && (
              <Scheduled
                isActiveTab={isActiveTab}
                place={place}
                scheduledOrderCount={orderCount.scheduledOrder}
                currentSubscribeToMenuOrderMoveToActive={
                  currentSubscribeToMenuOrderMoveToActive
                }
                setCurrentSubscribeToMenuOrderMoveToActive={
                  setCurrentSubscribeToMenuOrderMoveToActive
                }
              />
            )}
            {isActiveTab === 'cancelled' && (
              <Cancelled
                {...props}
                isActiveTab={isActiveTab}
                currentSubscribeToMenuOrderMoveToActive={
                  currentSubscribeToMenuOrderMoveToActive
                }
                setCurrentSubscribeToMenuOrderMoveToActive={
                  setCurrentSubscribeToMenuOrderMoveToActive
                }
              />
            )}
          </>
        </Container>
      </Box>
    </>
  );
};

export default MainForm;
